import React, { useState, useEffect, useCallback } from "react";
import { apiCall, useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from "recharts";
import { Users, Phone, UserPlus, Flame, Activity, PhoneCall, ThumbsUp, PhoneOff, CheckCircle, XCircle, PhoneForwarded, PhoneMissed, Clock, Timer, RefreshCw, Calendar, TrendingUp, TrendingDown, Globe, Radio, Snowflake, DollarSign } from "lucide-react";
import { toast } from "sonner";

const DAY_PRESETS = [
  { label: "Today", days: 1 },
  { label: "Yesterday", days: 1, isYesterday: true },
  { label: "7D",  days: 7  },
  { label: "14D", days: 14 },
  { label: "30D", days: 30 },
  { label: "60D", days: 60 },
  { label: "90D", days: 90 },
];

const COLORS = ["hsl(199, 89%, 48%)", "hsl(24, 95%, 53%)", "hsl(152, 69%, 41%)", "hsl(215, 16%, 47%)", "hsl(262, 52%, 47%)", "hsl(355, 78%, 56%)"];

const SOURCE_LABELS = {
  all: "All Sources",
  google_sheet: "Google Sheet",
  extension: "Extension",
  csv_upload: "CSV Upload",
};

const DEFAULT_SOURCES = [
  "google_sheet",
  "extension",
  "csv_upload",
];

export const getSourceLabel = (src) => {
  if (!src || src === "all") return "All Sources";
  if (src === "csv") return "CSV Upload";
  if (src === "api") return "Google Sheet";
  if (SOURCE_LABELS[src]) return SOURCE_LABELS[src];
  return src.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [agentReport, setAgentReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [selectedSite, setSelectedSite] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [days, setDays] = useState(1); // Default to Today
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [broadcastSummary, setBroadcastSummary] = useState(null);
  const [sheetSyncStatus, setSheetSyncStatus] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const isAdmin = user?.role === "admin";

  const getLocalDateString = (offsetDays = 0) => {
    const d = new Date();
    d.setDate(d.getDate() - offsetDays);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchDashboardData = useCallback(async (d = days, site = selectedSite, source = selectedSource, from = customFrom, to = customTo) => {
    setChartsLoading(true);
    try {
      let statsUrl = `/dashboard/stats?days=${d}&`;
      if (site && site !== "all") statsUrl += `site=${site}&`;
      if (source && source !== "all") statsUrl += `source=${source}&`;
      
      let chartsUrl = `/dashboard/charts?days=${d}&`;
      if (site && site !== "all") chartsUrl += `site=${site}&`;
      if (source && source !== "all") chartsUrl += `source=${source}&`;
      
      if (from && to) {
        statsUrl += `date_from=${from}&date_to=${to}&`;
        chartsUrl += `from_date=${from}&to_date=${to}&`;
      }
      const [s, c] = await Promise.all([
        apiCall("get", statsUrl),
        apiCall("get", chartsUrl),
      ]);
      setStats(s.data);
      setCharts(c.data);
      if (!isAdmin) {
        let agentUrl = `/agent/my-reports?days=${d}`;
        if (from && to) agentUrl += `&from=${from}&to=${to}`;
        const { data: ad } = await apiCall("get", agentUrl);
        setAgentReport(ad);
      }
      // Fetch Broadcast Summary
      apiCall("get", "/broadcast-reports/dashboard-summary").then(res => setBroadcastSummary(res.data)).catch(() => {});
    } catch (e) {
      console.error(e);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
      setChartsLoading(false);
    }
  }, [isAdmin, days, selectedSite, selectedSource, customFrom, customTo]);

  useEffect(() => {
    fetchDashboardData(days, selectedSite, selectedSource, customFrom, customTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDashboardData, days, selectedSite, selectedSource]);

  const handlePreset = (preset) => {
    if (preset.isYesterday) {
      const yesterdayStr = getLocalDateString(1);
      setDays(1);
      setCustomFrom(yesterdayStr);
      setCustomTo(yesterdayStr);
      setShowCustom(false);
      fetchDashboardData(1, selectedSite, selectedSource, yesterdayStr, yesterdayStr);
    } else {
      setDays(preset.days);
      setShowCustom(false);
      setCustomFrom("");
      setCustomTo("");
      fetchDashboardData(preset.days, selectedSite, selectedSource, "", "");
    }
  };

  const handleCustomApply = () => {
    if (!customFrom || !customTo) { toast.error("Select both from and to dates"); return; }
    fetchDashboardData(days, selectedSite, selectedSource, customFrom, customTo);
    setShowCustom(false);
  };

  const handleSiteChange = (site) => {
    setSelectedSite(site);
    fetchDashboardData(days, site, selectedSource, customFrom, customTo);
  };

  const handleSourceChange = (source) => {
    setSelectedSource(source);
    fetchDashboardData(days, selectedSite, source, customFrom, customTo);
  };

  const handleSheetSync = async () => {
    setSheetSyncStatus("syncing");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sheets/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sheet_id: "" }),
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Sync failed");
      setSheetSyncStatus("ok");
      setLastSyncTime(new Date());
      toast.success(`Sheets synced: ${data.created} created, ${data.updated} updated`);
      fetchDashboardData(days, selectedSite, selectedSource, customFrom, customTo);
    } catch (err) {
      setSheetSyncStatus("error");
      toast.error(err.message || "Sheets sync failed");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const siteDistribution = stats?.site_stats?.map(s => ({
    name: s.site || "Unknown",
    value: s.total || 0
  })) || [];

  const siteFinancials = stats?.site_stats?.map(s => ({
    name: s.site || "Unknown",
    deposit: s.total_deposit || 0,
    withdraw: s.total_withdraw || 0
  })) || [];

  const isYesterdayActive = customFrom && customFrom === getLocalDateString(1) && customTo === getLocalDateString(1);
  const isPeriodView = (days > 1) || (customFrom && customTo && !isYesterdayActive);
  const periodLabel = customFrom && customTo && !isYesterdayActive ? `${customFrom} to ${customTo}` : `${days}D`;

  const metrics = [
    { title: "Total Clients", value: stats?.total_clients || 0, icon: Users, color: "text-primary" },
    ...(isPeriodView ? [
      { title: `${periodLabel} New Clients`, value: stats?.period_new_clients || 0, icon: UserPlus, color: "text-emerald-500" },
      { title: `${periodLabel} Deposit`, value: `₹${Number(stats?.period_deposit || 0).toLocaleString('en-IN')}`, icon: TrendingUp, color: "text-emerald-600" },
      { title: `${periodLabel} Withdraw`, value: `₹${Number(stats?.period_withdraw || 0).toLocaleString('en-IN')}`, icon: TrendingDown, color: "text-rose-600" },
      { title: `${periodLabel} Net P&L`, value: `₹${Number(stats?.period_net_pnl || 0).toLocaleString('en-IN')}`, icon: DollarSign, color: (stats?.period_net_pnl || 0) >= 0 ? "text-emerald-600" : "text-rose-600" },
      { title: `${periodLabel} Calls`, value: stats?.period_calls || 0, icon: PhoneCall, color: "text-purple-500" },
    ] : [
      { title: isYesterdayActive ? "Yesterday Clients" : "Today Clients", value: isYesterdayActive ? (stats?.yesterday_reg || 0) : (stats?.today_reg || 0), icon: UserPlus, color: "text-emerald-500" },
      { title: isYesterdayActive ? "Today Clients" : "Yesterday Clients", value: isYesterdayActive ? (stats?.today_reg || 0) : (stats?.yesterday_reg || 0), icon: Calendar, color: "text-sky-500" },
    ]),
    { title: "Fresh Clients", value: stats?.fresh_clients || stats?.new_clients || 0, icon: UserPlus, color: "text-blue-500" },
    { title: "Hot Clients", value: stats?.hot_clients || 0, icon: Flame, color: "text-orange-500" },
    { title: "Regular Clients", value: stats?.regular_clients || 0, icon: CheckCircle, color: "text-emerald-600" },
    { title: "Cold Clients", value: stats?.cold_clients || 0, icon: Snowflake, color: "text-slate-500" },
    ...(isAdmin ? [{ title: "Active Agents", value: `${stats?.active_agents || 0}/${stats?.total_agents || 0}`, icon: Activity, color: "text-green-500" }] : []),
    ...(isAdmin ? [
      { title: "Today Deposit", value: `₹${Number(stats?.today_deposit || 0).toLocaleString('en-IN')}`, icon: TrendingUp, color: "text-emerald-600" },
      { title: "Today Withdraw", value: `₹${Number(stats?.today_withdraw || 0).toLocaleString('en-IN')}`, icon: TrendingDown, color: "text-rose-600" },
      { title: "Yesterday Deposit", value: `₹${Number(stats?.yesterday_deposit || 0).toLocaleString('en-IN')}`, icon: TrendingUp, color: "text-green-500" },
      { title: "Yesterday Withdraw", value: `₹${Number(stats?.yesterday_withdraw || 0).toLocaleString('en-IN')}`, icon: TrendingDown, color: "text-red-500" },
      { title: "Today Broadcast", value: broadcastSummary ? `${broadcastSummary.today?.done || 0} / ${(broadcastSummary.today?.done || 0) + (broadcastSummary.today?.pending || 0)}` : "-", icon: Radio, color: "text-blue-500" },
      { title: "Yesterday Broadcast", value: broadcastSummary ? `${broadcastSummary.yesterday?.done || 0} / ${(broadcastSummary.yesterday?.done || 0) + (broadcastSummary.yesterday?.missed || 0)}` : "-", icon: Radio, color: "text-indigo-500" },
      { title: "Delayed Deposits", value: stats?.today_delayed_deposits || 0, icon: Clock, color: "text-orange-500" },
      { title: "Avg Dep Delay", value: `${stats?.avg_deposit_delay || 0} min`, icon: Timer, color: "text-yellow-600" },
      { title: "Delayed Withdrawals", value: stats?.today_delayed_withdrawals || 0, icon: Clock, color: "text-rose-500" },
      { title: "Avg Wdr Delay", value: `${stats?.avg_withdraw_delay || 0} min`, icon: Timer, color: "text-amber-600" },
    ] : []),
    { title: "Calls Today", value: stats?.total_calls_today || 0, icon: PhoneCall, color: "text-purple-500" },
    { title: "Total Calls", value: stats?.total_calls || 0, icon: Phone, color: "text-primary" },
    { title: "Connected", value: stats?.connected || 0, icon: PhoneForwarded, color: "text-green-600" },
    { title: "Not Connected", value: stats?.not_connected || 0, icon: PhoneMissed, color: "text-red-500" },
    { title: "Interested", value: stats?.interested || 0, icon: ThumbsUp, color: "text-green-500" },
    { title: "Follow Up", value: stats?.follow_up || 0, icon: PhoneOff, color: "text-yellow-500" },
    { title: "Converted", value: stats?.converted || 0, icon: CheckCircle, color: "text-purple-500" },
  ];

  // Agent-specific metrics
  const agentMetrics = agentReport ? [
    { title: "Today Calls", value: agentReport.today.calls, icon: PhoneCall, color: "text-purple-500" },
    { title: "Today Connected", value: agentReport.today.connected, icon: PhoneForwarded, color: "text-green-600" },
    { title: "Today Not Conn.", value: agentReport.today.not_connected, icon: PhoneMissed, color: "text-red-500" },
    { title: "Total Talk Time", value: `${agentReport.total.duration_hours}h`, icon: Timer, color: "text-blue-500" },
    { title: "Login Hours", value: `${agentReport.login_hours}h`, icon: Clock, color: "text-green-500" },
    { title: "Active Hours", value: `${agentReport.active_hours}h`, icon: Activity, color: "text-primary" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* ── Top Header & Controls ── */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Welcome, {user?.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">{isAdmin ? "Admin overview" : "Your performance"}</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 flex-wrap">
              {sheetSyncStatus === "ok" && lastSyncTime && (
                <span className="text-[10px] text-green-600 flex items-center gap-1">
                  ✓ Synced {lastSyncTime.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}
                </span>
              )}
              {sheetSyncStatus === "error" && (
                <span className="text-[10px] text-red-500">Sync failed</span>
              )}
              <Button
                variant="outline" size="sm"
                onClick={handleSheetSync}
                disabled={sheetSyncStatus === "syncing"}
                className="flex items-center gap-1.5 text-xs h-8"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${sheetSyncStatus === "syncing" ? "animate-spin" : ""}`} />
                {sheetSyncStatus === "syncing" ? "Syncing..." : "Sync Sheets"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate("/broadcast-reports")} className="text-xs h-8">
                <Radio className="w-3.5 h-3.5 mr-1 text-primary" /> Broadcast Reports
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate("/b2c-reports")} className="text-xs h-8">
                B2C Reports
              </Button>
            </div>
          )}
        </div>

        {/* ── Broadcast Status Card on Admin Overview ── */}
        {broadcastSummary && (
          <div className="p-3 rounded-lg bg-card border border-border flex flex-wrap items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                <Radio className="w-4 h-4 text-primary" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                  Broadcast Overview (Telegram / WhatsApp / SMS / Brand)
                </span>
                <div className="flex items-center gap-3 mt-0.5 text-xs">
                  <span className="text-muted-foreground">Today:</span>
                  <span className="font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> {broadcastSummary.today?.done || 0} Done
                  </span>
                  <span className="font-bold text-amber-600 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {broadcastSummary.today?.pending || 0} Pending
                  </span>
                  <span className="text-muted-foreground ml-2 border-l border-border pl-2">Yesterday:</span>
                  <span className="font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> {broadcastSummary.yesterday?.done || 0} Done
                  </span>
                  <span className="font-bold text-rose-600 flex items-center gap-1">
                    <XCircle className="w-3.5 h-3.5" /> {broadcastSummary.yesterday?.missed || 0} Missed
                  </span>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/broadcast-reports")}
              className="text-xs h-7 px-2.5 text-primary font-medium hover:underline ml-auto"
            >
              Open Broadcast Reports &rarr;
            </Button>
          </div>
        )}

        {/* ── Filters Bar: Site Select + Date Range (Default: Today) ── */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-card border border-border shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            {/* Site Selector */}
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedSite} onValueChange={handleSiteChange}>
                <SelectTrigger className="h-8 w-44 text-xs font-medium">
                  <SelectValue placeholder="Select Site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {(stats?.available_sites || []).map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Selector */}
            <div className="flex items-center gap-2 ml-2">
              <Select value={selectedSource || "all"} onValueChange={handleSourceChange}>
                <SelectTrigger className="h-8 w-36 text-xs font-medium">
                  <SelectValue placeholder="Select Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {DEFAULT_SOURCES.map(s => (
                    <SelectItem key={s} value={s}>{getSourceLabel(s)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Presets (Default: Today) */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {DAY_PRESETS.map((preset) => {
                const isActive = preset.isYesterday
                  ? isYesterdayActive
                  : (days === preset.days && !customFrom && !isYesterdayActive);
                return (
                  <Button
                    key={preset.label}
                    size="sm"
                    variant={isActive ? "default" : "outline"}
                    className="h-8 px-3 text-xs font-medium"
                    onClick={() => handlePreset(preset)}
                  >
                    {preset.label}
                  </Button>
                );
              })}
              <Button
                size="sm"
                variant={showCustom || (customFrom && !isYesterdayActive) ? "default" : "outline"}
                className="h-8 px-3 text-xs font-medium"
                onClick={() => setShowCustom(v => !v)}
              >
                Custom
              </Button>
            </div>
          </div>

          {chartsLoading && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              <span>Updating...</span>
            </div>
          )}

          {showCustom && (
            <div className="w-full flex items-center gap-2 pt-2 border-t border-border flex-wrap">
              <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-8 text-xs w-36" />
              <span className="text-xs text-muted-foreground">to</span>
              <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-8 text-xs w-36" />
              <Button size="sm" className="h-8 px-3 text-xs" onClick={handleCustomApply}>Apply Range</Button>
            </div>
          )}
        </div>
      </div>

      {/* Agent-specific stats row */}
      {!isAdmin && agentReport && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {agentMetrics.map((m, i) => (
            <Card key={i} className="border border-border"><CardContent className="p-3"><div className="flex items-center justify-between mb-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{m.title}</span><m.icon className={`w-3.5 h-3.5 ${m.color}`} /></div><p className="text-xl font-bold">{m.value}</p></CardContent></Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="metrics-grid">
        {metrics.map((m, i) => (
          <Card key={i} className="border border-border hover:-translate-y-0.5 transition-all duration-200" data-testid={`metric-${m.title.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-3"><div className="flex items-center justify-between mb-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{m.title}</span><m.icon className={`w-3.5 h-3.5 ${m.color}`} /></div><p className="text-xl font-bold tracking-tight">{m.value}</p></CardContent>
          </Card>
        ))}
      </div>

      {/* ── Broadcast Reports Summary Widget ── */}
      {broadcastSummary && (
        <Card className="border border-border bg-card/60">
          <CardHeader className="p-3 pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Radio className="w-4 h-4 text-primary" /> Daily Broadcast Status (Telegram / WhatsApp / Brand)
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/broadcast-reports")}
              className="text-xs h-7 px-2 text-primary font-medium hover:underline"
            >
              View Broadcast Reports &rarr;
            </Button>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Today Broadcasts */}
              <div className="p-2.5 rounded-lg border border-border bg-background flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-muted-foreground">Today's Broadcasts</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> {broadcastSummary.today?.done || 0} Done
                    </span>
                    <span className="text-xs text-muted-foreground">|</span>
                    <span className="text-sm font-bold text-amber-600 flex items-center gap-1">
                      <Clock className="w-4 h-4" /> {broadcastSummary.today?.pending || 0} Pending
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-mono text-muted-foreground">{broadcastSummary.today?.date}</span>
                </div>
              </div>

              {/* Yesterday Broadcasts */}
              <div className="p-2.5 rounded-lg border border-border bg-background flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-muted-foreground">Yesterday's Broadcasts</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> {broadcastSummary.yesterday?.done || 0} Done
                    </span>
                    <span className="text-xs text-muted-foreground">|</span>
                    <span className="text-sm font-bold text-rose-600 flex items-center gap-1">
                      <XCircle className="w-4 h-4" /> {broadcastSummary.yesterday?.missed || 0} Missed
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-mono text-muted-foreground">{broadcastSummary.yesterday?.date}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 transition-opacity duration-200 ${chartsLoading ? "opacity-50 pointer-events-none" : "opacity-100"}`}>
        {/* Calls Over Time */}
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{isAdmin ? "Calls Over Time" : "My Calls Over Time"}</CardTitle></CardHeader>
          <CardContent><div className="h-64"><ResponsiveContainer width="100%" height="100%">
            <AreaChart data={!isAdmin && agentReport ? agentReport.daily : (charts?.calls_per_day || [])}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} stroke="hsl(var(--muted-foreground))" /><YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" /><Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} /><Area type="monotone" dataKey="calls" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer></div></CardContent>
        </Card>

        {/* Agent: Hourly Calls Today */}
        {!isAdmin && agentReport && (
          <Card className="border border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">My Hourly Calls Today</CardTitle></CardHeader>
            <CardContent><div className="h-64"><ResponsiveContainer width="100%" height="100%">
              <BarChart data={agentReport.hourly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="hour" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" /><YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" /><Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} /><Bar dataKey="calls" fill={COLORS[0]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer></div></CardContent>
          </Card>
        )}

        {/* Response Distribution */}
        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{isAdmin ? "Call Responses" : "My Responses"}</CardTitle></CardHeader>
          <CardContent><div className="h-64"><ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={charts?.response_distribution || []} cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={3} dataKey="value" label={({ name, value }) => value > 0 ? `${name}: ${value}` : ""}>{(charts?.response_distribution || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer></div></CardContent>
        </Card>

        {/* Admin: Agent Performance */}
        {isAdmin && charts?.agent_performance?.length > 0 && (
          <Card className="border border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Agent Performance</CardTitle></CardHeader>
            <CardContent><div className="h-64"><ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.agent_performance}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" /><YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" /><Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} /><Legend /><Bar dataKey="calls" fill={COLORS[0]} radius={[4, 4, 0, 0]} name="Calls" /><Bar dataKey="interested" fill={COLORS[3]} radius={[4, 4, 0, 0]} name="Interested" /></BarChart>
            </ResponsiveContainer></div></CardContent>
          </Card>
        )}

        {/* Admin: Client Categories */}
        {isAdmin && (
          <Card className="border border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Client Categories</CardTitle></CardHeader>
            <CardContent><div className="h-64"><ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={charts?.category_distribution || []} cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={3} dataKey="value" label={({ name, value }) => value > 0 ? `${name}: ${value}` : ""}>{(charts?.category_distribution || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /><Legend /></PieChart>
            </ResponsiveContainer></div></CardContent>
          </Card>
        )}

        {/* Site Distribution */}
        {isAdmin && siteDistribution.length > 0 && (
          <Card className="border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Clients by Site</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie 
                      data={siteDistribution} 
                      cx="50%" 
                      cy="50%" 
                      innerRadius={45} 
                      outerRadius={85} 
                      paddingAngle={3} 
                      dataKey="value" 
                      label={({ name, value }) => value > 0 ? `${name}: ${value}` : ""}
                    >
                      {siteDistribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financial Overview by Site */}
        {isAdmin && siteFinancials.length > 0 && (
          <Card className="border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Deposits vs Withdrawals by Site</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={siteFinancials}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                    <Legend />
                    <Bar dataKey="deposit" fill={COLORS[0]} radius={[4, 4, 0, 0]} name="Total Deposit" />
                    <Bar dataKey="withdraw" fill={COLORS[1]} radius={[4, 4, 0, 0]} name="Total Withdraw" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
